import { Tag, Tooltip, Button } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { displayNumbers } from '../../../../variables/numbersFunctions';

const PRIMARY = '#242166';

// ── « Par branche » (ex « Vue globale ») : colonnes FIXES, sans colonne dynamique par
//    bénéficiaire (qui provoquait le scroll horizontal). La répartition par bénéficiaire est
//    affichée via une LIGNE EXTENSIBLE (cf. repartitionExpandColumns + StatistiquesSAP.js).
//    Le param compagniesBeneficiaires est conservé pour compat (ignoré ici).
export const statistiquesGlobalesColumns = () => [
    {
        title: 'Exercice',
        dataIndex: 'exercice',
        key: 'exercice',
        width: 90,
        sorter: (a, b) => a.exercice - b.exercice,
        render: (value) => <Tag color="blue">{value}</Tag>,
    },
    {
        title: 'Branche',
        dataIndex: 'categorieLibelle',
        key: 'branche',
        render: (libelle, record) => (
            <Tag color="purple" style={{ fontWeight: 600 }}>{record.categorieCode}</Tag>
        ),
    },
    {
        title: 'Compagnie cédante',
        dataIndex: 'compagnieCedanteNom',
        key: 'compagnieCedante',
        ellipsis: true,
        render: (nom, record) => (
            <div>
                <div style={{ fontWeight: 500 }}>{nom}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{record.compagnieCedanteCode}</div>
            </div>
        ),
    },
    {
        title: 'Nbre sin.',
        dataIndex: 'nombreSinistres',
        key: 'nombreSinistres',
        width: 100,
        align: 'center',
        sorter: (a, b) => a.nombreSinistres - b.nombreSinistres,
        render: (value) => <strong>{value}</strong>,
    },
    {
        title: 'SAP',
        dataIndex: 'montantTotalSap',
        key: 'sap',
        width: 150,
        align: 'right',
        sorter: (a, b) => a.montantTotalSap - b.montantTotalSap,
        render: (value) => <strong style={{ color: '#dc2626' }}>{displayNumbers(value)} F</strong>,
    },
    {
        title: '% éval.',
        key: 'pct',
        width: 90,
        align: 'center',
        render: (_, record) => {
            const pct = record.montantTotalEvaluation > 0
                ? (record.montantTotalSap / record.montantTotalEvaluation) * 100 : 0;
            return (
                <Tooltip title={`SAP / évaluation`}>
                    <span style={{ color: '#6b7280', fontSize: 12 }}>{pct.toFixed(1)}%</span>
                </Tooltip>
            );
        },
    },
];

// Mini-tableau de la répartition par bénéficiaire (ligne extensible).
export const repartitionExpandColumns = () => [
    { title: 'Bénéficiaire', dataIndex: 'beneficiaireNom', key: 'nom', render: (n, r) => (<span><strong>{n}</strong> <span style={{ color: '#9ca3af', fontSize: 11 }}>{r.beneficiaireCode}</span></span>) },
    { title: 'Taux', dataIndex: 'tauxRepartition', key: 'taux', width: 100, align: 'center', render: (v) => <Tag color="geekblue">{(v || 0).toFixed(2)}%</Tag> },
    { title: 'Montant SAP', dataIndex: 'montantSAP', key: 'montant', width: 160, align: 'right', render: (v) => <strong style={{ color: PRIMARY }}>{displayNumbers(v || 0)} F</strong> },
];

// ── « Détail des sinistres » : colonnes FIXES (la répartition est dans la modale au clic œil).
export const detailSinistresColumns = (compagniesBeneficiaires = [], onVoirDetail) => [
    {
        title: 'N° Sinistre',
        dataIndex: 'numeroSinistre',
        key: 'numeroSinistre',
        width: 150,
        render: (value, record) => (
            <div>
                <div style={{ fontWeight: 500 }}>{value || 'N/A'}</div>
                <Tag color="blue" style={{ fontSize: 10 }}>{record.compagnieCedanteCode}</Tag>
            </div>
        ),
    },
    {
        title: 'Survenance',
        dataIndex: 'dateSinistre',
        key: 'dateSinistre',
        width: 110,
        render: (date) => (date ? dayjs(date).format('DD/MM/YYYY') : 'N/A'),
        sorter: (a, b) => dayjs(a.dateSinistre).unix() - dayjs(b.dateSinistre).unix(),
    },
    { title: 'N° Police', dataIndex: 'numeroPolice', key: 'numeroPolice', width: 130, ellipsis: true },
    { title: 'Souscripteur', dataIndex: 'souscripteur', key: 'souscripteur', ellipsis: true },
    {
        title: 'Branche',
        dataIndex: 'categorieCode',
        key: 'branche',
        width: 90,
        render: (code) => <Tag color="purple">{code}</Tag>,
    },
    {
        title: 'Statut',
        dataIndex: 'statutSinistre',
        key: 'statut',
        width: 110,
        render: (statut) => {
            const color = statut === '1' ? 'green' : statut === '2' ? 'orange' : 'default';
            const label = statut === '1' ? 'EN INSTANCE' : statut === '2' ? 'OUVERT' : 'TERMINÉ';
            return <Tag color={color}>{label}</Tag>;
        },
    },
    {
        title: 'SAP',
        dataIndex: 'sap',
        key: 'sap',
        width: 140,
        align: 'right',
        sorter: (a, b) => a.sap - b.sap,
        render: (value) => <strong style={{ color: '#dc2626' }}>{displayNumbers(value)} F</strong>,
    },
    {
        title: '',
        key: 'action',
        width: 60,
        align: 'center',
        render: (_, record) => (
            <Tooltip title="Voir la répartition">
                <Button type="text" size="small" icon={<EyeOutlined style={{ color: PRIMARY }} />} onClick={() => onVoirDetail(record)} />
            </Tooltip>
        ),
    },
];

// ── « Par compagnie » (résumé bénéficiaire) : libellé Branche.
export const resumeParBeneficiaireColumns = () => [
    { title: 'Exercice', dataIndex: 'exercice', key: 'exercice', width: 90, render: (value) => <Tag color="blue">{value}</Tag> },
    {
        title: 'Compagnie',
        dataIndex: 'beneficiaireNom',
        key: 'beneficiaire',
        ellipsis: true,
        render: (nom, record) => (
            <div>
                <div style={{ fontWeight: 600 }}>{nom}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{record.beneficiaireCode}</div>
            </div>
        ),
    },
    { title: 'Branche', dataIndex: 'categorieCode', key: 'branche', width: 100, render: (code) => <Tag color="purple">{code}</Tag> },
    { title: 'Taux', dataIndex: 'tauxRepartition', key: 'taux', width: 90, align: 'center', render: (value) => <Tag color="geekblue">{(value || 0).toFixed(2)}%</Tag> },
    { title: 'Nb sin.', dataIndex: 'nombreSinistres', key: 'nombreSinistres', width: 90, align: 'center', render: (value) => <strong>{value}</strong> },
    {
        title: 'SAP réparti',
        dataIndex: 'montantSapReparti',
        key: 'montantSap',
        width: 160,
        align: 'right',
        sorter: (a, b) => a.montantSapReparti - b.montantSapReparti,
        render: (value) => <strong style={{ color: '#dc2626' }}>{displayNumbers(value)} F</strong>,
    },
];
